import { exitSupportSession } from '../services/supportDataService';
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import { UserProfile } from '../models/userProfile';
import { StaffProfile } from '../models/staffProfile';
import { useAppState } from '../context/AppStateContext';
import { UserDataService } from '../services/userDataService';
import PasswordReauthModal from './PasswordReauthModal';
import MfaSetupModal from './MfaSetupModal';
import { clearSelectedOrgId } from '../services/selectedOrg';

interface Props {
  user: UserProfile;
  activeUser: StaffProfile | null;
  showMenu: boolean;
  onClose: (event: MouseEvent) => void;
  onChangePassword: () => void;
  onLockDevice: () => void;
  onLogout: () => void;
}

const UserProfilePopup: React.FC<Props> = ({ user, activeUser, showMenu, onClose, onChangePassword, onLockDevice, onLogout }) => {
  const { roles, selectedOrganisation, setUser, setUserRoleIds, setActiveUser, setActiveUserToken, setSelectedOrganisation } = useAppState();
  const navigate = useNavigate();
  const [showSwitchAuth, setShowSwitchAuth] = useState(false);
  const [showMfa, setShowMfa] = useState(false);
  const [workspaceCount, setWorkspaceCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    UserDataService.fetchOrganisations()
      .then(orgs => { if (!cancelled) setWorkspaceCount(orgs.length); })
      .catch(() => { if (!cancelled) setWorkspaceCount(null); });
    return () => { cancelled = true; };
  }, []);

  const handleSwitchWorkspace = () => {
    setShowSwitchAuth(true);
  };

  /**
   * Org Lock gate confirmed.
   *  - sameUser=true  → morning user just re-confirmed; keep their app-state user
   *                     loaded but clear org/PIN selection.
   *  - sameUser=false → a different Cognito user just authed and is now the
   *                     morning user. Wipe app-state user too so the next
   *                     screen reloads it from the new Cognito session.
   */
  const onSwitchPasswordConfirmed = (sameUser: boolean) => {
    setShowSwitchAuth(false);
    sessionStorage.removeItem('activeUserToken');
    clearSelectedOrgId({ forgetLastUsed: true });
    setActiveUserToken(null);
    setActiveUser(null);
    setSelectedOrganisation(null);
    if (!sameUser) {
      setUser(null);
      setUserRoleIds([]);
    }
    navigate('/select-org');
  };
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        onClose(event);
      }
    }

    function handleEsc(event: any) {
      if (event.key === 'Escape') {
        onClose(null as any);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [popupRef]);

  const showSwitchWorkspace = workspaceCount === null || workspaceCount > 1;
  const isDeviceUserAlsoActive =
    !!activeUser?.email &&
    !!user?.email &&
    activeUser.email.trim().toLowerCase() === user.email.trim().toLowerCase();

  const roleNameFor = (id: number) => Object.entries(roles).find(([, v]) => v === id)?.[0] || `Role ${id}`;

  const handleSwitchUser = () => {
    onClose(null as any);
    navigate('/staff-select');
  };

  return (
    <div ref={popupRef} className={`user-profile-panel ${showMenu ? 'show' : ''}`}>
      <h5 className="font-primary user-profile-panel__title">Account</h5>

      {/* SECTION 1: WORKSPACE */}
      <div className="user-profile-section">
        <div className="user-profile-section__title">Workspace</div>
        <div className="user-profile-section__card">
          <div className="user-profile-row">
            <span className="user-profile-row__label">Workspace</span>
            <span className="user-profile-row__value">{selectedOrganisation?.name || 'None'}</span>
            {/* Support leaves through "Exit support session" below instead. */}
            {showSwitchWorkspace && selectedOrganisation && !activeUser?.isSupport && (
              <button
                type="button"
                className="user-profile-row__action"
                title="Switch workspace"
                onClick={handleSwitchWorkspace}
              >
                <i className="fas fa-right-left"></i>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* SECTION 2: ACTIVE USER */}
      {activeUser && (
        <div className="user-profile-section">
          <div className="user-profile-section__title">Active user</div>
          <div className="user-profile-section__card">
            <div className="user-profile-identity">
              <div className="user-profile-identity__name">{activeUser.name}</div>
              <div className="user-profile-identity__email">{activeUser.email}</div>
              {activeUser.isSupport ? (
                // Support acts with Admin-equivalent roles internally; showing
                // "Admin" here would misdescribe a time-limited visit.
                <div className="user-profile-pills">
                  <span className="user-profile-pill user-profile-pill--role">Rustle It Support</span>
                </div>
              ) : (activeUser.roleIds.length > 0 || activeUser.isExternal) && (
                <div className="user-profile-pills">
                  {activeUser.roleIds.map(id => (
                    <span key={id} className="user-profile-pill user-profile-pill--role">{roleNameFor(id)}</span>
                  ))}
                  {activeUser.isExternal && (
                    <span className="user-profile-pill user-profile-pill--external">External</span>
                  )}
                </div>
              )}
            </div>
            <div className="user-profile-section__actions">
              {activeUser.isSupport ? (
                // Support is not staff: no one to switch to, and no PIN to
                // unlock with. Leaving is the only session action.
                <div className="sidebar-nav-item" onClick={exitSupportSession}>
                  <i className="fas fa-door-open"></i> Exit support session
                </div>
              ) : (
                <>
                  <div className="sidebar-nav-item" onClick={handleSwitchUser}>
                    <i className="fas fa-user-group"></i> Switch user
                  </div>
                  <div className="sidebar-nav-item" data-help="account.lock-device" onClick={onLockDevice}>
                    <i className="fas fa-lock"></i> Lock device
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* SECTION 3: DEVICE SESSION */}
      <div className="user-profile-section">
        <div className="user-profile-section__title">Device session</div>
        <div className="user-profile-section__card">
          <div className="user-profile-identity">
            <div className="user-profile-identity__label">Signed in as</div>
            <div className="user-profile-identity__email user-profile-identity__email--device">
              {user?.email}
              {isDeviceUserAlsoActive && <span className="user-profile-identity__you"> (you)</span>}
            </div>
          </div>
          <div className="user-profile-section__actions">
            {/* Gated by severity, not by subject. These two CHANGE the
                credentials of the account the device signs in with, so they
                belong to the person that account is — a PIN-switched colleague
                changing the password behind the machine, or enrolling
                two-factor on somebody else's account, is not theirs to do.
                (MFA is the sharper case: enrolling it does not ask for the
                password, so nothing else stood in the way.) */}
            {isDeviceUserAlsoActive && (
              <>
                <div className="sidebar-nav-item" data-help="account.change-password" onClick={onChangePassword}>
                  <i className="fas fa-key"></i> Change device password
                </div>
                <div className="sidebar-nav-item" data-help="account.two-factor" onClick={() => setShowMfa(true)}>
                  <i className="fas fa-shield-halved"></i> Two-factor authentication
                </div>
              </>
            )}
            {/* Sign out stays open to whoever is at the machine. It ends a
                session rather than changing an account: nothing is destroyed
                and the cost is one sign-in. Whoever is last here at the end of
                the day should be able to shut it properly, and the morning
                user may have gone home. Gating it would also have been
                theatre — the profile picker's Sign out has no such check, so
                the only thing a gate here adds is two clicks and a rule that
                needs explaining. */}
            <div className="sidebar-nav-item sidebar-nav-item--destructive" data-help="account.sign-out" onClick={onLogout}>
              <i className="fas fa-right-from-bracket"></i> Sign out
            </div>
          </div>
        </div>
      </div>

      {showMfa && <MfaSetupModal onClose={() => setShowMfa(false)} />}

      {showSwitchAuth && (
        <PasswordReauthModal
          defaultEmail={user?.email || ''}
          title="Switch workspace"
          description="Sign in with your email and password to switch to a different organisation. The current PIN session is cleared. If you sign in as a different user, that user becomes the device's primary user."
          confirmLabel="Sign in & switch"
          onConfirmed={onSwitchPasswordConfirmed}
          onCancel={() => setShowSwitchAuth(false)}
        />
      )}
    </div>
  )
}

export default UserProfilePopup
